import React from "react";
import Field from "../Field.tsx";
import SmartIcon from "../SmartIcon.tsx";
import {SeriesCharacterInfo} from "../../models/interfaces/series.interface.ts";
import {allGendersOptions, CharacterGender} from "../../models/types/charecterGender.ts";
import Select from "../Select.tsx";
import {IItemsSelect} from "../../models/interfaces/selectItems.interface.ts";
import {titleCase} from "../../utils/utils.ts";
import {BiMaleFemale} from "react-icons/bi";
import NumberRangeField from "../NumberRangeField.tsx";

interface SeriesCharacterExpendedDetailsProps {
  isOpen: boolean;
  character: SeriesCharacterInfo;
  editNameGenderAge: (characterId: string, value: string | CharacterGender, field: "name" | "gender" | "ageRange") => void;
  setOtherNames: (characterId: string, otherNames: string[]) => void;
}

const genders: IItemsSelect[] = allGendersOptions.map((gender: string) => {
  return {
    id: gender,
    title: titleCase(gender),
  }
})

const SeriesCharacterExpendedDetails: React.FC<SeriesCharacterExpendedDetailsProps> = (
  {
    isOpen,
    character,
    editNameGenderAge,
    setOtherNames
  }: SeriesCharacterExpendedDetailsProps
) => {

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    editNameGenderAge(character.characterId, event.target.value, "name");
  };

  const handleGenderChange = (updatedItem: IItemsSelect) => {
    editNameGenderAge(character.characterId, updatedItem.id as CharacterGender, "gender");
  }

  const handleAgeRangeChange = (value: string) => {
    editNameGenderAge(character.characterId, value, "ageRange");
  };

  const handleAddOtherName = () => {
    const updatedOtherNames: string[] = [...character.otherNames, `name #${character.otherNames.length + 1}`];
    setOtherNames(character.characterId, updatedOtherNames);
  }

  const handleOtherNameChange = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const updatedOtherNames: string[] = [...character.otherNames];
    updatedOtherNames[index] = event.target.value;
    setOtherNames(character.characterId, updatedOtherNames);
  };

  const handleRemoveOtherName = (index: number) => {
    const updatedOtherNames: string[] = [...character.otherNames];
    updatedOtherNames.splice(index, 1);
    setOtherNames(character.characterId, updatedOtherNames);
  };

  return (
    <div
      className={`pl-10 text-sm text-n-300 overflow-hidden transition-all duration-950 ease-in-out transform-gpu origin-top ${
        isOpen ? 'max-h-[1000px] opacity-100 py-2' : 'max-h-0 opacity-0 py-0'
      }`}
    >
      <div className="flex items-center justify-between w-full pointer-events-auto gap-4">
        <Field
          className="w-full"
          label="Name"
          placeholder="Character Name"
          value={character.name}
          onChange={handleNameChange}
          required
        />
        <Select
          className="w-full"
          classButton="h-13 rounded-xl shadow-[inset_0_0_0_0.0625rem_#DADBDC] caption1 dark:shadow-[inset_0_0_0_0.0625rem_#2A2E2F] dark:bg-transparent"
          classOptions="min-w-full overflow-auto max-h-40"
          classIcon="w-5 h-5 fill-n-400/50"
          classArrow="fill-n-400"
          icon={BiMaleFemale}
          placeholder="Gender"
          items={genders}
          value={{
            id: character?.gender ?? "male",
            title: titleCase(character?.gender ?? "male")
          }}
          onChange={handleGenderChange}
          label="Gender"
        />
        <NumberRangeField
          className="w-full"
          label="Age Range"
          placeholder="0-100"
          value={character.ageRange}
          onChange={handleAgeRangeChange}
        />
      </div>
      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between w-full">
          <div className="my-6 text-md font-semibold">Other Names</div>
          <div
            className="py-1 px-2 text-sm border border-n-400 rounded-xl hover:border-brandPurple-500 hover:text-brandPurple-500 cursor-pointer active:scale-95 transition-transform"
            onClick={handleAddOtherName}
          >
            + Add
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {character.otherNames.map((name: string, idx: number) => (
            <div
              key={idx}
              className="flex items-center justify-between w-full gap-4 p-2 bg-n-700 rounded-lg"
            >
              <Field
                className="w-full"
                placeholder="Other Name"
                value={name}
                onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleOtherNameChange(idx, event)}
                required
              />
              <div className="cursor-pointer" onClick={() => handleRemoveOtherName(idx)}>
                <SmartIcon
                  name="delete-chat"
                  className="w-5 h-5 fill-bloodCrimson-400/80 hover:fill-bloodCrimson-400"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeriesCharacterExpendedDetails;